import { parseSidebarCustomLinks } from '@/helpers/ppgHtmlParser';
import request from '@/helpers/request';
import templateBuilder from '@/helpers/templateBuilder';

export default {
  state: {
    quickLinks: {},
    additionalContainer: '',
    compatibleProducts: {},
    faqLink: {},
    crossSellProducts: {},
    upSellProducts: {},
    manualsLink: {},
    showSidebar: false,
  },
  getters: {
    getShowSidebar(state) {
      return state.showSidebar;
    },
    getSidebarQuickLinks(state) {
      return state.quickLinks;
    },
    getSidebarAdditional(state) {
      return state.additionalContainer;
    },
    getSidebarAdditionalContent(state, getters) {
      const additionalContainer = getters.getSidebarAdditional;
      return additionalContainer ? additionalContainer.content : '';
    },
    getShowSidebarAdditionalContent(state, getters) {
      const content = getters.getSidebarAdditionalContent;
      const showSidebar = getters.getShowSidebar;
      return content && showSidebar;
    },
    getSoftwareLink(state, getters) {
      const quicklinks = getters.getSidebarQuickLinks;
      return quicklinks.softwareLink;
    },
    getShowSoftwareLink(state, getters) {
      const quicklinks = getters.getSidebarQuickLinks;
      return quicklinks.showSoftware;
    },
    getCompatibleProducts(state) {
      return state.compatibleProducts;
    },
    getCompatibleProductsByProductId(state, getters) {
      return (productId) => getters.getCompatibleProducts[productId];
    },
    getActiveCompatibleProducts(state, getters) {
      const productId = getters.getProductId;
      const products = getters.getCompatibleProductsByProductId(productId);
      return products || [];
    },
    getHasActiveCompatibleProducts(state, getters) {
      return !!getters.getActiveCompatibleProducts.length;
    },
    getFaqLink(state) {
      return state.faqLink;
    },
    getFaqLinkByPartNumber(state, getters) {
      return (partNumber) => getters.getFaqLink[partNumber];
    },
    getActiveFaqLink(state, getters) {
      const partNumber = getters.getPartNumber;
      const faqLink = getters.getFaqLinkByPartNumber(partNumber);
      return faqLink || null;
    },
    getCrossSellProducts(state) {
      return state.crossSellProducts;
    },
    getCrossSellProductsByProductId(state, getters) {
      return (productId) => getters.getCrossSellProducts[productId];
    },
    getActiveCrossSellProducts(state, getters) {
      const productId = getters.getProductId;
      const crossSellProducts = getters.getCrossSellProductsByProductId(productId);
      return crossSellProducts || [];
    },
    getUpSellProducts(state) {
      return state.upSellProducts;
    },
    getUpSellProductsByProductId(state, getters) {
      return (productId) => getters.getUpSellProducts[productId];
    },
    getActiveUpSellProducts(state, getters) {
      const productId = getters.getProductId;
      const upSellProducts = getters.getUpSellProductsByProductId(productId);
      return upSellProducts || [];
    },
    getManualsLink(state) {
      return state.manualsLink;
    },
    getManualsLinkByPartNumber(state, getters) {
      return (partNumber) => getters.getManualsLink[partNumber];
    },
    getActiveManualsLink(state, getters) {
      const partNumber = getters.getPartNumber;
      const manualsLink = getters.getManualsLinkByPartNumber(partNumber);
      return manualsLink || null;
    },
  },
  mutations: {
    setCompatibleProducts(state, payload) {
      const { data, productId } = payload;
      state.compatibleProducts = { ...state.compatibleProducts, [productId]: data };
    },
    setFaqLink(state, payload) {
      const { data, partNumber } = payload;
      state.faqLink = { ...state.faqLink, [partNumber]: data };
    },
    setCrossSellProducts(state, payload) {
      const { data, productId } = payload;
      state.crossSellProducts = { ...state.crossSellProducts, [productId]: data };
    },
    setUpSellProducts(state, payload) {
      const { data, productId } = payload;
      state.upSellProducts = { ...state.upSellProducts, [productId]: data };
    },
    setManualsLink(state, payload) {
      const { data, partNumber } = payload;
      state.manualsLink = { ...state.manualsLink, [partNumber]: data };
    },
    setQuickLinks(state, payload) {
      state.quickLinks = payload;
    },
    setAdditionalContainer(state, payload) {
      state.additionalContainer = payload;
    },
    setShowSidebar(state, payload) {
      state.showSidebar = payload;
    },
  },
  actions: {
    setSidebar({ commit }, payload) {
      if (payload) {
        const { content } = payload.quickLinks;
        payload.quickLinks.content = parseSidebarCustomLinks(content);
        commit('setQuickLinks', payload.quickLinks);
        commit('setAdditionalContainer', payload.additionalContainer);
        commit('setShowSidebar', true);
      }
    },
    async callCompatibleProducts({ commit, getters }) {
      const productId = getters.getProductId;
      const products = getters.getCompatibleProducts;
      const urlTemplate = getters.getCompatibleProductsJsonUrl;

      if (!urlTemplate || products[productId]) {
        return;
      }

      const url = templateBuilder(urlTemplate, { productId });

      const data = await request({
        url,
      }).catch(() => {
        throw new Error('Unable to call compatibleProducts service');
      });

      const productsCache = {
        productId,
        data,
      };

      commit('setCompatibleProducts', productsCache);
    },
    async callFaqLink({ commit, getters }) {
      const partNumber = getters.getPartNumber;
      const faqLink = getters.getFaqLink;
      const urlTemplate = getters.getFaqJsonUrl;

      if (!urlTemplate || faqLink[partNumber]) {
        return;
      }

      const url = templateBuilder(urlTemplate, { partNum: partNumber });

      const data = await request({
        url,
        headers: {
          'Content-Type': 'text',
        },
      }).catch(() => {
        throw new Error('Unable to call faqLink service');
      });

      const faqLinkCache = {
        partNumber,
        data,
      };

      commit('setFaqLink', faqLinkCache);
    },
    async callCrossSellProducts({ commit, getters }) {
      const productId = getters.getProductId;
      const products = getters.getCrossSellProducts;
      const urlTemplate = getters.getCrossSellsJsonUrl;

      if (!urlTemplate || products[productId]) {
        return;
      }

      const url = templateBuilder(urlTemplate, { productId });

      const data = await request({
        url,
      }).catch(() => {
        throw new Error('Unable to call crossSellProducts service');
      });

      const productsCache = {
        productId,
        data,
      };

      commit('setCrossSellProducts', productsCache);
    },
    async callUpSellProducts({ commit, getters }) {
      const productId = getters.getProductId;
      const products = getters.getUpSellProducts;
      const urlTemplate = getters.getUpSellsJsonUrl;

      if (!urlTemplate || products[productId]) {
        return;
      }

      const url = templateBuilder(urlTemplate, { productId });

      const data = await request({
        url,
      }).catch(() => {
        throw new Error('Unable to call upSellProducts service');
      });

      const productsCache = {
        productId,
        data,
      };

      commit('setUpSellProducts', productsCache);
    },
    async callManualsLink({ commit, getters }) {
      const partNumber = getters.getPartNumber;
      const manualsLink = getters.getManualsLink;
      const manualsJsonUrl = getters.getManualsJsonUrl;
      const urlTemplate = getters.getManualsUrl;
      const locale = getters.getLocale;
      const localeParts = locale.split('-');

      if (!urlTemplate || manualsLink[partNumber]) {
        return;
      }
      // get boolean returned by getManualsJsonUrl
      // to see if manuals is being displayed
      const url = templateBuilder(manualsJsonUrl, { partNum: partNumber });

      const showManuals = await request({
        url,
      }).catch(() => {
        throw new Error('Unable to call manualsJsonUrl');
      });

      const dataUrl = templateBuilder(urlTemplate, {
        language: localeParts[0],
        country: localeParts[1],
        partNum: partNumber,
      });

      const data = {
        url: dataUrl,
        showManuals,
      };

      const manualsLinkCache = {
        partNumber,
        data,
      };
      commit('setManualsLink', manualsLinkCache);
    },
  },
};
